import { useUpdate } from 'react-admin';
import { useGetAppConfig } from './useGetAppConfig';
import { useOnSettled } from './useOnSettled';
import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Switch,
  TextField,
  Tooltip,
  IconButton,
  Typography,
} from '@mui/material';
import { Semaphore } from './Semaphore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

export const DiarizationEnrollmentSettings = () => {
  const [updateConfig, { isLoading: isUpdating }] = useUpdate(
    'therapistAppConfig',
    { id: 'current' },
  );

  const { remoteAppConfig, refetchAppConfig } = useGetAppConfig();
  const ff_transcription_speakerEnrollment = !!(
    remoteAppConfig as {
      ff_transcription_speakerEnrollment?: number;
    }
  )?.ff_transcription_speakerEnrollment;

  const savedBatchMs = Number(
    (remoteAppConfig as { transcriptionServerBatchDurationMs?: number })
      ?.transcriptionServerBatchDurationMs ?? 60_000,
  );
  const savedMergeM = Number(
    (remoteAppConfig as { transcriptionMergeMaxMultiplier?: number })
      ?.transcriptionMergeMaxMultiplier ?? 3,
  );
  const savedMinWords = Number(
    (
      remoteAppConfig as {
        transcriptionSpeakerEnrollmentMinWordsPerSegment?: number;
      }
    )?.transcriptionSpeakerEnrollmentMinWordsPerSegment ?? 5,
  );

  const [batchMs, setBatchMs] = useState(String(savedBatchMs));
  const [mergeM, setMergeM] = useState(String(savedMergeM));
  const [minWords, setMinWords] = useState(String(savedMinWords));

  useEffect(() => {
    setBatchMs(String(savedBatchMs));
    setMergeM(String(savedMergeM));
    setMinWords(String(savedMinWords));
  }, [savedBatchMs, savedMergeM, savedMinWords]);

  const { onSettled: doSettled } = useOnSettled();

  const onSettled = useCallback(async () => {
    doSettled();
    await refetchAppConfig();
  }, [doSettled, refetchAppConfig]);

  const onSwitch = useCallback(async () => {
    await updateConfig(
      'therapistAppConfig',
      {
        id: 'current',
        data: {
          ff_transcription_speakerEnrollment:
            ff_transcription_speakerEnrollment ? 0 : 1,
        },
      },
      { onSettled },
    );
  }, [updateConfig, onSettled, ff_transcription_speakerEnrollment]);

  const onSaveNumbers = useCallback(async () => {
    const b = Math.max(5000, Math.floor(Number(batchMs)) || 60_000);
    const m = Math.max(1, Math.floor(Number(mergeM)) || 3);
    const w = Math.max(1, Math.floor(Number(minWords)) || 5);
    await updateConfig(
      'therapistAppConfig',
      {
        id: 'current',
        data: {
          transcriptionServerBatchDurationMs: b,
          transcriptionMergeMaxMultiplier: m,
          transcriptionSpeakerEnrollmentMinWordsPerSegment: w,
        },
      },
      { onSettled },
    );
  }, [updateConfig, onSettled, batchMs, mergeM, minWords]);

  const numbersDirty =
    batchMs !== String(savedBatchMs) ||
    mergeM !== String(savedMergeM) ||
    minWords !== String(savedMinWords);

  return (
    <Box marginTop="2rem">
      <Box display="flex" alignItems="center" gap={0.5} marginBottom={1}>
        <Typography variant="h6">
          Speaker enrollment pipeline
        </Typography>
        <Tooltip
          title="Requires server chunks transcription. Session-note–centric server-side batching, optional speaker enrollment blobs, and transcriptionPipeline on session notes. Per-therapist overrides available under therapist App config."
        >
          <IconButton size="small" sx={{ padding: 0.5 }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
        <Switch
          checked={ff_transcription_speakerEnrollment}
          onChange={onSwitch}
          disabled={isUpdating}
        />
        {!isUpdating && (
          <Semaphore enabled={ff_transcription_speakerEnrollment} />
        )}
        {isUpdating && <CircularProgress size={14} />}
      </Box>

      <Box marginTop={2} display="flex" flexDirection="column" gap={2} maxWidth={480}>
        <TextField
          label="Server batch duration (ms)"
          value={batchMs}
          onChange={e => setBatchMs(e.target.value)}
          type="number"
          size="small"
          helperText="Target audio length per transcription request (default 60000)."
        />
        <TextField
          label="Merge max multiplier (N)"
          value={mergeM}
          onChange={e => setMergeM(e.target.value)}
          type="number"
          size="small"
          helperText="Recovery merge cap: up to N × batch duration in one request (default 3)."
        />
        <TextField
          label="Enrollment min words per diarized segment"
          value={minWords}
          onChange={e => setMinWords(e.target.value)}
          type="number"
          size="small"
          helperText="Only segments with at least this many words can be stitched into a composite enrollment clip (default 5)."
        />
        <Typography variant="body2" color="text.secondary" component="p">
          <strong>Note:</strong> If this value is set very high, some speakers may
          never accumulate 5 seconds of eligible audio. Enrollment for those
          labels is skipped until a later batch has enough qualifying speech.
        </Typography>
        <Button
          variant="outlined"
          onClick={onSaveNumbers}
          disabled={isUpdating || !numbersDirty}
        >
          Save batching settings
        </Button>
      </Box>
    </Box>
  );
};
